import type { ReactNode } from "react"
import { useMemo, useState } from "react"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconArrowLeftOutline18,
	IconMagnifierOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	HubspotIcon,
	IconButton,
	Input,
	IntegrationCard,
	IntercomIcon,
	LinearIcon,
	Shortcut,
	SlackIcon,
} from "@productlane/ui"

import type { NavItem } from "../../app/sidebar"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { SettingsHeader } from "../../settings/settings-header"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SETTINGS_CARD_CLASS,
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import {
	ACCOUNT_NAV_ITEMS,
	ALL_SETTINGS_NAV_ITEMS,
	ALL_SETTINGS_SUBPAGE_NAV_ITEMS,
	FEATURES_NAV_ITEMS,
	SETUP_NAV_ITEMS,
	WORKSPACE_NAV_ITEMS,
} from "../../settings/sidebar"

type Version = "A" | "A1" | "A2" | "A3" | "B" | "C"
type FieldSize = "sm" | "md" | "lg"

const VERSIONS: { version: Version; title: string; line: string }[] = [
	{
		version: "A",
		title: "Search field",
		line: "One large field. Results open inside the card as you type.",
	},
	{
		version: "A1",
		title: "Compact field, floating results",
		line: "A slimmer field the height of an input. Results float over the page, so nothing below moves.",
	},
	{
		version: "A2",
		title: "Large field, grouped results",
		line: "A taller field with bigger type. Results sort under their nav group.",
	},
	{
		version: "A3",
		title: "Pinned on every page",
		line: "Sits above the title on every settings page and stays put while you scroll. Click the nav to switch pages.",
	},
	{
		version: "B",
		title: "Search with shortcuts",
		line: "The field plus six pages people open most. Typing swaps them for results.",
	},
	{
		version: "C",
		title: "Search with the full index",
		line: "The field plus every settings page by group. Typing filters the list in place.",
	},
]

const GROUPS: { title: string; items: NavItem[] }[] = [
	{ title: "Administration", items: WORKSPACE_NAV_ITEMS },
	{ title: "Personal", items: ACCOUNT_NAV_ITEMS },
	{ title: "Setup", items: SETUP_NAV_ITEMS },
	{ title: "Features", items: FEATURES_NAV_ITEMS },
]

const SHORTCUT_HREFS = [
	"/settings/members",
	"/settings/plans",
	"/settings/integrations",
	"/settings/ai",
	"/settings/widget",
	"/settings/email",
]

const SEARCHABLE: NavItem[] = [
	...ALL_SETTINGS_NAV_ITEMS,
	...ALL_SETTINGS_SUBPAGE_NAV_ITEMS,
].filter(
	(item, index, all) => all.findIndex((i) => i.href === item.href) === index,
)

function matches(item: NavItem, query: string) {
	const q = query.trim().toLowerCase()
	if (!q) return true
	return (
		item.title.toLowerCase().includes(q) ||
		(item.keywords ?? "").toLowerCase().includes(q)
	)
}

function groupOf(href: string) {
	return GROUPS.find((g) => g.items.some((i) => i.href === href))?.title
}

function PageRow({ item, hint }: { item: NavItem; hint?: string }) {
	const Icon = item.icon
	return (
		<div className="group/row flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800">
			<Icon
				strokeWidth={1}
				className="h-4 w-4 flex-none transform-gpu text-gray-800 dark:text-gray-400"
			/>
			<span className="flex-1 truncate text-sm text-primary">{item.title}</span>
			{hint && <span className="text-xs text-tertiary">{hint}</span>}
		</div>
	)
}

const FIELD_CLASS: Record<FieldSize, { input: string; icon: string }> = {
	sm: {
		input: "h-9 rounded-none !bg-transparent pl-9 text-sm",
		icon: "left-3 h-4 w-4",
	},
	md: {
		input: "h-11 rounded-none !bg-transparent pl-11 text-base",
		icon: "left-4 h-[18px] w-[18px]",
	},
	lg: {
		input: "h-14 rounded-none !bg-transparent pl-12 text-lg",
		icon: "left-4 h-5 w-5",
	},
}

function SearchField({
	query,
	setQuery,
	size = "md",
}: {
	query: string
	setQuery: (q: string) => void
	size?: FieldSize
}) {
	return (
		<Input
			name="settings-search-card"
			variant="borderless"
			inputClassName={FIELD_CLASS[size].input}
			iconClassName={cn(
				FIELD_CLASS[size].icon,
				"text-gray-800 dark:text-gray-400",
			)}
			placeholder="Search settings"
			icon={IconMagnifierOutline18}
			value={query}
			onChange={(e) => setQuery(e.target.value)}
			suffix={
				<span className="flex w-6 flex-none items-center justify-center pr-2">
					{query ? (
						<IconButton
							variant="ghost"
							size="sm"
							Icon={IconXmarkOutline18}
							aria-label="Clear search"
							onClick={() => setQuery("")}
						/>
					) : (
						<Shortcut variant="ghost" live={false}>
							/
						</Shortcut>
					)}
				</span>
			}
		/>
	)
}

function Results({ query }: { query: string }) {
	const results = SEARCHABLE.filter((item) => matches(item, query)).slice(0, 8)
	if (results.length === 0) {
		return (
			<div className="px-4 py-3 text-sm text-tertiary">
				No settings match {`"${query}"`}
			</div>
		)
	}
	return (
		<div className="flex flex-col gap-0.5 p-2">
			{results.map((item) => (
				<PageRow key={item.href} item={item} hint={groupOf(item.href)} />
			))}
		</div>
	)
}

function GroupedResults({ query }: { query: string }) {
	const groups = GROUPS.map((group) => ({
		...group,
		items: SEARCHABLE.filter(
			(item) => groupOf(item.href) === group.title && matches(item, query),
		),
	})).filter((group) => group.items.length > 0)
	if (groups.length === 0) {
		return (
			<div className="px-4 py-3 text-sm text-tertiary">
				No settings match {`"${query}"`}
			</div>
		)
	}
	return (
		<div className="flex flex-col gap-3 p-2 pt-3">
			{groups.map((group) => (
				<div key={group.title} className="flex flex-col gap-0.5">
					<div className="px-2 pb-1 text-xs text-tertiary">{group.title}</div>
					{group.items.map((item) => (
						<PageRow key={item.href} item={item} />
					))}
				</div>
			))}
		</div>
	)
}

function FloatingSearch({ size }: { size: FieldSize }) {
	const [query, setQuery] = useState("")
	const searching = query.trim().length > 0
	return (
		<div className="relative">
			<div className={cn(SETTINGS_CARD_CLASS, "overflow-hidden")}>
				<SearchField query={query} setQuery={setQuery} size={size} />
			</div>
			{searching && (
				<div
					className={cn(
						"absolute inset-x-0 top-full z-20 mt-1.5 overflow-hidden rounded-[10px] bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10",
					)}
				>
					<Results query={query} />
				</div>
			)}
		</div>
	)
}

function SearchCard({ version }: { version: Version }) {
	const [query, setQuery] = useState("")
	const searching = query.trim().length > 0
	const shortcuts = useMemo(
		() =>
			SHORTCUT_HREFS.map((href) =>
				SEARCHABLE.find((item) => item.href === href),
			).filter((item): item is NavItem => !!item),
		[],
	)

	if (version === "A1" || version === "A3") {
		return (
			<div className="relative">
				<NewBadge />
				<FloatingSearch size="sm" />
			</div>
		)
	}

	return (
		<div className="relative">
			<NewBadge />
			<div className={cn(SETTINGS_CARD_CLASS, "overflow-hidden")}>
				<SearchField
					query={query}
					setQuery={setQuery}
					size={version === "A2" ? "lg" : "md"}
				/>

				{searching && (
					<div className="border-t border-gray-200 dark:border-gray-700/80">
						{version === "C" ? (
							<IndexGrid query={query} />
						) : version === "A2" ? (
							<GroupedResults query={query} />
						) : (
							<Results query={query} />
						)}
					</div>
				)}

				{!searching && version === "B" && (
					<div className="grid grid-cols-3 gap-0.5 border-t border-gray-200 p-2 dark:border-gray-700/80">
						{shortcuts.map((item) => (
							<PageRow key={item.href} item={item} />
						))}
					</div>
				)}

				{!searching && version === "C" && (
					<div className="border-t border-gray-200 dark:border-gray-700/80">
						<IndexGrid query="" />
					</div>
				)}
			</div>
		</div>
	)
}

function NewBadge() {
	return (
		<Badge variant="important" className="absolute -left-16 top-2">
			New
		</Badge>
	)
}

function IndexGrid({ query }: { query: string }) {
	const groups = GROUPS.map((group) => ({
		...group,
		items: group.items.filter((item) => matches(item, query)),
	})).filter((group) => group.items.length > 0)

	if (groups.length === 0) {
		return (
			<div className="px-4 py-3 text-sm text-tertiary">
				No settings match {`"${query}"`}
			</div>
		)
	}
	return (
		<div className="grid grid-cols-2 gap-x-2 gap-y-4 p-2 pt-3">
			{groups.map((group) => (
				<div key={group.title} className="flex flex-col gap-0.5">
					<div className="px-2 pb-1 text-xs text-tertiary">{group.title}</div>
					{group.items.map((item) => (
						<PageRow key={item.href} item={item} />
					))}
				</div>
			))}
		</div>
	)
}

function NavReplica({
	activeHref = "/settings/general",
	onSelect,
}: {
	activeHref?: string
	onSelect?: (item: NavItem) => void
}) {
	return (
		<div className="w-[15.1rem] flex-none overflow-y-auto border-r border-gray-300 bg-gray-100 pb-20 dark:border-gray-700 dark:bg-gray-950">
			<div className="px-2.5 pb-2 pt-2.5">
				<div className="flex items-center rounded-md py-1 pl-2 pr-2">
					<IconArrowLeftOutline18
						strokeWidth={1}
						className="mr-3 h-4 w-4 flex-none text-tertiary"
					/>
					<span className="text-xs text-tertiary">Back to app</span>
				</div>
			</div>
			{GROUPS.map((group) => (
				<div
					key={group.title}
					className="mt-2 flex flex-col gap-0.5 px-2.5 first:mt-1"
				>
					<div className="pb-1 pl-2 text-xs text-tertiary">{group.title}</div>
					{group.items.map((item) => {
						const Icon = item.icon
						const active = item.href === activeHref
						return (
							<button
								type="button"
								key={item.href}
								onClick={() => onSelect?.(item)}
								className={cn(
									"flex w-full items-center rounded-md py-1 pl-2 pr-2 text-left",
									active
										? "bg-gray-150 dark:bg-gray-800"
										: "hover:bg-gray-150 hover:dark:bg-gray-800",
								)}
							>
								<Icon
									strokeWidth={1}
									className="mr-3 h-4 w-4 flex-none text-gray-800 dark:text-gray-400"
								/>
								<span
									className={cn(
										"truncate text-sm font-medium",
										active ? "text-primary" : "text-secondary",
									)}
								>
									{item.title}
								</span>
							</button>
						)
					})}
				</div>
			))}
		</div>
	)
}

const INTEGRATIONS = [
	{ name: "Linear", Icon: LinearIcon },
	{ name: "Slack", Icon: SlackIcon },
	{ name: "Hubspot", Icon: HubspotIcon },
	{ name: "Intercom migrator", Icon: IntercomIcon },
]

function GeneralPageReplica({ version }: { version: Version }) {
	return (
		<>
			<SettingsHeader title="General" />
			<div className="flex flex-col gap-2">
				{version !== "A3" && (
					<div className="mb-8">
						<SearchCard version={version} />
					</div>
				)}

				<div className="text-sm font-medium">Workspace settings</div>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Logo"
						description="256×256 px recommended. SVG may not display in emails."
						action={
							<div className="h-14 w-14 rounded-xl bg-gray-900 dark:bg-gray-700" />
						}
					/>
					<SettingItem
						title="Workspace name"
						className="pt-4"
						description="Display name for your workspace."
						action={
							<Input
								name="workspace-name"
								defaultValue="Productlane"
								className="max-w-60"
							/>
						}
					/>
				</SettingsContainer>

				<div className="mt-8 flex items-center justify-between">
					<div className="text-sm font-medium">Integrations</div>
					<Button variant="ghost" size="sm" iconRight={ChevronRight}>
						View all
					</Button>
				</div>
				<div className="mt-1 grid grid-cols-3 gap-3 sm:-mx-3.5">
					{INTEGRATIONS.map(({ name, Icon }) => (
						<IntegrationCard
							key={name}
							variant="compact"
							name={name}
							connected
							logo={<Icon className="h-5 w-5" />}
						/>
					))}
				</div>

				<div className="mt-8 text-sm font-medium">Danger zone</div>
				<SettingsContainer className="mt-1">
					<SettingItem
						title="Leave workspace"
						description="Remove yourself from this workspace. You can be re-invited later."
						action={<Button variant="danger-ghost">Leave workspace</Button>}
					/>
					<SettingItem
						className="pt-4"
						title="Delete workspace"
						description="Permanently delete this workspace and all its data. This action is irreversible."
						action={<Button variant="danger-ghost">Delete workspace</Button>}
					/>
				</SettingsContainer>
			</div>
		</>
	)
}

function OtherPageReplica({ item }: { item: NavItem }) {
	return (
		<>
			<SettingsHeader title={item.title} />
			<div className="flex flex-col gap-2">
				<div className="text-xs text-tertiary">
					Placeholder content. Only the pinned search is real here.
				</div>
				{[0, 1, 2].map((section) => (
					<SettingsContainer key={section} className="mt-1">
						{[0, 1, 2].map((row) => (
							<SettingItem
								key={row}
								className={row > 0 ? "pt-4" : undefined}
								title={`${item.title} setting ${section * 3 + row + 1}`}
								description="One line that says what this setting does."
								action={
									<div className="h-5 w-9 rounded-full bg-gray-200 dark:bg-gray-700" />
								}
							/>
						))}
					</SettingsContainer>
				))}
			</div>
		</>
	)
}

function PinnedSearchFrame() {
	const [page, setPage] = useState<NavItem | null>(null)
	return (
		<>
			<NavReplica
				activeHref={page?.href ?? "/settings/general"}
				onSelect={(item) =>
					setPage(item.href === "/settings/general" ? null : item)
				}
			/>
			<div className="flex-1 overflow-y-auto">
				<div className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-950">
					<div className="mx-auto w-full max-w-[704px] px-5 pb-3 pt-4">
						<SearchCard version="A3" />
					</div>
				</div>
				<main className="mx-auto w-full max-w-[704px] px-5 pb-16 pt-8">
					{page ? (
						<OtherPageReplica item={page} />
					) : (
						<GeneralPageReplica version="A3" />
					)}
				</main>
			</div>
		</>
	)
}

function VersionFrame({
	version,
	title,
	line,
	children,
}: {
	version: Version
	title: string
	line: string
	children: ReactNode
}) {
	return (
		<div className="space-y-3">
			<div className="space-y-0.5">
				<div className="text-sm font-semibold text-primary">
					Version {version}: {title}
				</div>
				<div className="text-xs text-secondary">{line}</div>
			</div>
			<div className="flex h-[960px] w-[1280px] overflow-hidden rounded-xl bg-gray-100 ring-1 ring-black/10 dark:bg-gray-950 dark:ring-white/15">
				{children}
			</div>
		</div>
	)
}

export function SettingsSearchCardPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<SettingsCardInsetContext.Provider value={true}>
			<div className="min-h-screen space-y-16 overflow-x-auto bg-white px-10 py-10 pb-24 dark:bg-gray-900">
				<div className="max-w-3xl space-y-1">
					<div className="text-lg font-semibold text-primary">
						Settings search on the General page
					</div>
					<p className="text-sm text-secondary">
						The search moves out of the settings nav and becomes the first card
						on General. Each version is a working replica: type in the card to
						see its results. Only the card marked New changes.
					</p>
				</div>
				{VERSIONS.map(({ version, title, line }) => (
					<VersionFrame
						key={version}
						version={version}
						title={title}
						line={line}
					>
						{version === "A3" ? (
							<PinnedSearchFrame />
						) : (
							<>
								<NavReplica />
								<div className="flex-1 overflow-y-auto">
									<main className="mx-auto w-full max-w-[704px] px-5 py-16">
										<GeneralPageReplica version={version} />
									</main>
								</div>
							</>
						)}
					</VersionFrame>
				))}
			</div>
		</SettingsCardInsetContext.Provider>
	)
}
